import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SITE, waLink } from "@/lib/site";
import { ALL_FAQS, FAQ_CATEGORIES } from "@/data/content";

export default function Faqs() {
  const [cat, setCat] = useState("All");

  const filtered = useMemo(
    () => (cat === "All" ? ALL_FAQS : ALL_FAQS.filter((f) => f.cat === cat)),
    [cat]
  );

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: ALL_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    breadcrumbSchema([
      { name: "Home", url: "https://entspecialistvaranasi.com/" },
      { name: "ENT FAQs", url: "https://entspecialistvaranasi.com/ent-faqs-varanasi" },
    ]),
  ];

  return (
    <>
      <Seo
        title="ENT FAQs in Varanasi — Ear, Nose, Throat & Vertigo | Dr. Sanjay Teza"
        description="25 detailed answers to common ENT questions in Varanasi covering ear, nose, throat, vertigo and children's ENT — by ENT specialist Dr. Sanjay Teza."
        path="/ent-faqs-varanasi"
        schemas={schemas}
      />

      {/* Header */}
      <section className="pt-36 md:pt-44 pb-16 bg-brand-mint/25 border-b border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <nav className="flex items-center gap-2 text-xs text-brand-muted mb-8" data-testid="faq-breadcrumb">
              <Link to="/" className="hover:text-brand-pine">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-brand-ink">ENT FAQs</span>
            </nav>
            <p className="text-xs tracking-[0.2em] uppercase text-brand-pine mb-4">Knowledge Base</p>
            <h1 className="font-heading font-bold text-brand-ink text-5xl md:text-6xl lg:text-7xl leading-[1.03] tracking-tight max-w-4xl">
              ENT FAQs in Varanasi
            </h1>
            <p className="mt-6 text-lg text-brand-muted max-w-2xl leading-relaxed">
              Clear, doctor-reviewed answers to the questions patients ask most — across ear, nose,
              throat, vertigo and children&apos;s ENT care.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filter + list */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2.5 mb-12" data-testid="faq-filter-group">
            {FAQ_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`faq-filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`rounded-full px-5 py-2 text-sm transition-colors duration-200 border ${
                  cat === c
                    ? "bg-brand-teal text-brand-ink border-brand-teal"
                    : "bg-brand-surface text-brand-ink border-brand-line hover:border-brand-pine"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {filtered.map((f, i) => (
              <AccordionItem key={`${cat}-${i}`} value={`item-${i}`} className="border-brand-line">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-heading text-xl md:text-2xl text-brand-ink py-6 hover:no-underline gap-6"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="text-sm font-body text-brand-pine/60 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {f.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-brand-muted pb-6 pl-10">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA strip */}
      <section className="pb-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="rounded-3xl bg-brand-ink text-brand-bg p-10 md:p-14 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl">Still have a question?</h2>
            <p className="mt-4 text-brand-bg/60 max-w-lg mx-auto">
              Every patient is different. Message {SITE.doctorName} directly and get a clear answer
              about your specific concern.
            </p>
            <a
              href={waLink("Hello, I have a question about an ENT concern.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-cta-whatsapp-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-whatsapp px-8 py-3.5 text-sm text-white transition-colors hover:bg-whatsapp-hover"
            >
              Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
